import { Button, Dialog, DialogBody, DialogContent, DialogTrigger, FullScreenLoading } from '@civilgrid/ui/components';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import type { FeatureCollection, MultiPolygon } from 'geojson';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { useSnapshot } from 'valtio';
import { listDrfRequests as apiListRequests } from '~/api/drf';
import { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { drfStatusLabels } from '~/common/drfUtils';
import { useQuery as useSearchParams } from '~/common/routerUtils';
import { DrfRequestStatus, type MapFulfillmentRequest } from '~/common/types';
import Table, { GlobalFilter, Pagination } from '~/components2/Table';
import { useIsMounted } from '~/hooks/useIsMounted';
import { useOrgFeatureFlag } from '~/hooks/useOrgFeatureFlag';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import Charts from './Charts';
import { columns } from './columns';
import {
  ExportTable,
  getLayerIdsForMultipleAgencies,
  getMetadataIdsForAgenciesLayers,
  isCNPOrg,
  skipNavColumns,
} from './common';
import RequestSheet from './RequestSheet';
import RequestsMap from './RequestsMap';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateRow?: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

export default function DataRequestFulfillment() {
  useOrgFeatureFlag({ flag: OrgFeatureFlag.Drf });
  const { currentOrganizationId, currentOrganizationDetail } = useSnapshot(rootStore);
  const [data, setData] = useState<MapFulfillmentRequest[]>([]);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const closeMap = useCallback(() => setIsMapOpen(false), []);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const search = useSearchParams();

  const requests = useAsync(async () => {
    if (!currentOrganizationId) return [];
    const res = await apiListRequests(currentOrganizationId);
    if (isMounted()) {
      setData(Array.isArray(res) ? [...res] : []);
    }
  }, [currentOrganizationId]);

  useEffect(() => {
    const previousLayerIds = Array.from(layerStore.visibleLayerIds);

    if (!currentOrganizationDetail.isLoading) {
      const orgAgencyId = currentOrganizationDetail.orgAgencyId;
      if (orgAgencyId) {
        const agencyLayerIds = getLayerIdsForMultipleAgencies(orgAgencyId);
        layerStore.visibleLayerIds.clear();
        agencyLayerIds.forEach((layerId) => {
          layerStore.visibleLayerIds.add(layerId);
        });

        const metadataIds = getMetadataIdsForAgenciesLayers(orgAgencyId);
        layerStore.visibleMetadataIds.clear();
        metadataIds.forEach((metadataId) => {
          layerStore.visibleMetadataIds.add(metadataId);
        });
      }
    }

    return () => {
      layerStore.visibleLayerIds.clear();
      previousLayerIds.forEach((layerId) => {
        layerStore.visibleLayerIds.add(layerId);
      });
    };
  }, [currentOrganizationDetail]);

  const features = useMemo<FeatureCollection<MultiPolygon>>(() => {
    return {
      type: 'FeatureCollection',
      features: data.map((d) => ({
        type: 'Feature',
        geometry: d.geometry,
        properties: {
          id: d.id,
          requestId: d.requestId,
          name: d.name,
          requestor: d.requestor,
          client: d.client,
          area: d.areaInAcres,
          status: d.requestStatus,
        },
      })),
    };
  }, [data]);

  const isCriticalInfraVisible = useMemo<boolean>(() => {
    if (!currentOrganizationDetail.isLoading && !!currentOrganizationDetail.orgAgencyId) {
      return isCNPOrg(currentOrganizationDetail.orgAgencyId);
    }

    return false;
  }, [currentOrganizationDetail]);

  const isLoading = requests.loading === true;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (row) => row.id,
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
      sorting: [{ id: 'createdAt', desc: true }],
    },
    meta: {
      updateRow: (rowIndex, columnId, value) => {
        const keys = columnId.split('.');

        setData((prev) => {
          const copy = [...prev];
          const matched = copy[rowIndex];
          if (matched) {
            let curr: any = matched;
            for (let i = 0; i < keys.length - 1; i++) {
              const property = keys[i];
              curr = curr[property];
            }

            const propertyToUpdate = keys[keys.length - 1];
            curr[propertyToUpdate] = value;
          }

          copy[rowIndex] = matched;
          return copy;
        });
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const v = search.get('requestStatus') as DrfRequestStatus | null;
    const c = table.getColumn('requestStatus');
    if (!!v && drfStatusLabels[v]) {
      const columnValue = drfStatusLabels[v];
      c?.setFilterValue(columnValue);
    } else {
      c?.setFilterValue('');
    }
  }, [search.get('requestStatus')]);

  function onRowClick(row: Row<MapFulfillmentRequest>) {
    const newParams = new URLSearchParams(search);
    newParams.set('requestId', row.id);
    navigate({ to: `/dataRequestFulfillment?${newParams.toString()}` });
  }

  if (isLoading) return <FullScreenLoading />;
  const rs = search.get('requestStatus');

  return (
    <ProtectedRouteLayout fluidHeight includeOrgProjectSwitcher>
      <div className="space-y-4 overflow-clip p-4">
        <Charts data={data} />

        <div className="relative flex grow flex-col  p-2">
          <Table
            id="map-fulfillment-request-table"
            table={table}
            onRowClick={onRowClick}
            onCellClick={(cell) => {
              const { id } = cell.column;
              if (!skipNavColumns.has(id)) {
                onRowClick(cell.row);
              }
            }}
            topSection={
              <div className="py-2">
                <div className="my-2 flex items-center justify-start  gap-4">
                  <StatusLink isActive={!rs} />

                  <StatusLink
                    isActive={rs === DrfRequestStatus.Pending}
                    requestStatus={DrfRequestStatus.Pending}
                    label="Ready"
                  />

                  <StatusLink
                    isActive={rs === DrfRequestStatus.PaymentPending}
                    requestStatus={DrfRequestStatus.PaymentPending}
                  />

                  <StatusLink
                    isActive={rs === DrfRequestStatus.NeedsReview}
                    requestStatus={DrfRequestStatus.NeedsReview}
                  />
                </div>

                <div className="flex items-end justify-between">
                  <Pagination table={table} />

                  <div className="grid grid-cols-4 gap-2">
                    <ExportTable table={table} />
                    <Dialog open={isMapOpen} onOpenChange={setIsMapOpen}>
                      <DialogTrigger asChild>
                        <Button variant="secondary" data-testid="drf-map-view-button">
                          Map View
                        </Button>
                      </DialogTrigger>
                      <DialogContent
                        title="Requests map"
                        titleProps={{ className: 'sr-only' }}
                        className="z-[110] w-auto min-w-min max-w-[75vw]"
                        overlayClassName="z-[100]"
                        data-testid="drf-requests-map-dialog"
                      >
                        <DialogBody>
                          <RequestsMap
                            isCriticalInfraVisible={isCriticalInfraVisible}
                            requests={features}
                            closeDialog={closeMap}
                          />
                        </DialogBody>
                      </DialogContent>
                    </Dialog>

                    <div className="col-span-2">
                      <GlobalFilter table={table} />
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>

      <RequestSheet table={table} setData={setData} isCriticalInfraVisible={isCriticalInfraVisible} />
    </ProtectedRouteLayout>
  );
}

const StatusLink = ({
  requestStatus,
  isActive,
  label: _label,
}: {
  requestStatus?: DrfRequestStatus;
  isActive?: boolean;
  label?: string;
}) => {
  let to = `/dataRequestFulfillment`;

  if (requestStatus) {
    to += `?requestStatus=${requestStatus}`;
  }

  let label = '';

  if (_label) {
    label = _label;
  } else {
    label = requestStatus ? drfStatusLabels[requestStatus] : 'All';
  }

  return (
    <Link
      to={to}
      className={classNames('rounded px-1', {
        'bg-grey-20': !!isActive,
      })}
    >
      {label}
    </Link>
  );
};
